import { BaseLLMAdapter } from './BaseLLMAdapter';
import { ModelConfig } from '../../config';
import { SceneData } from '../../types';
import OpenAI from 'openai';

/**
 * 腾讯混元Turbos模型适配器 (OpenAI兼容模式)
 */
export class HunYuanAdapter extends BaseLLMAdapter {
  private client: OpenAI;
  
  /**
   * 构造函数
   * @param config 模型配置
   * @param debugMode 是否处于调试模式
   */
  constructor(config: ModelConfig, debugMode: boolean = false) {
    super(config, debugMode);
    
    const apiKey = this.getApiKey();
    const baseURL = this.config.endpoint || 'https://api.hunyuan.cloud.tencent.com/v1';
    
    // 调试信息
    this.logDebug('初始化HunYuan适配器', {
      apiKey: `${apiKey.substring(0, 8)}...`,
      baseURL,
      debugMode
    });
    
    // 初始化OpenAI客户端
    this.client = new OpenAI({
      apiKey,
      baseURL,
      dangerouslyAllowBrowser: true // 允许在浏览器中使用
    });
  }

  /**
   * 获取API密钥
   * @returns API密钥
   */
  protected getApiKey(): string {
    const apiKey = import.meta.env.VITE_TENCENT_OPENAI_API_KEY;
    
    // 调试信息
    this.logDebug('获取API密钥', {
      keyExists: !!apiKey,
      keyPrefix: apiKey ? apiKey.substring(0, 8) : 'undefined'
    });
    
    if (!apiKey) {
      throw new Error('未找到腾讯混元OpenAI兼容API密钥，请在环境变量中设置VITE_TENCENT_OPENAI_API_KEY');
    }
    
    if (!apiKey.startsWith('sk-')) {
      throw new Error('API密钥格式错误，OpenAI兼容模式需要以 sk- 开头的密钥');
    }
    
    return apiKey;
  }

  /**
   * 准备请求
   * @param prompt 提示文本
   * @returns 包含端点、请求头和请求体的对象
   */
  async prepareRequest(prompt: string): Promise<{
    endpoint: string;
    headers: Headers;
    body: any;
  }> {
    try {
      const body: OpenAI.Chat.ChatCompletionCreateParams = {
        model: this.config.id,  // 修正：使用正确的模型名称
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        stream: false, // 禁用流式响应
        temperature: 0.7,
        max_tokens: 2048
      };

      // 调试信息
      this.logDebug('准备HunYuan请求', {
        endpoint: this.config.endpoint,
        model: body.model,
        prompt: prompt.substring(0, 100) + (prompt.length > 100 ? '...' : ''),
        params: body
      });

      return {
        endpoint: `${this.config.endpoint}/chat/completions`,
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getApiKey()}`
        }),
        body
      };
    } catch (error) {
      this.logger.error('准备HunYuan请求时出错', (error as Error).message);
      throw error;
    }
  }

  /**
   * 发送请求
   * @param prompt 提示文本
   * @returns 响应数据
   */
  async sendRequest(prompt: string): Promise<Response> {
    try {
      const { body } = await this.prepareRequest(prompt);
      
      this.logDebug('发送HunYuan请求', {
        url: `${this.config.endpoint}/chat/completions`,
        body
      });
      
      const completion = await this.client.chat.completions.create(body);
      
      this.logDebug('收到HunYuan响应', completion);
      
      // 直接返回原始响应，让processResponse处理格式转换
      return new Response(JSON.stringify(completion), {
        status: 200,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
    } catch (error) {
      this.logger.error('发送HunYuan请求时出错', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }

  /**
   * 处理响应
   * @param response HunYuan的原始响应
   * @returns 处理后的场景数据
   */
  async processResponse(response: Response): Promise<SceneData | null> {
    try {
      const data = await response.json();
      
      this.logDebug('处理HunYuan响应', data);
      
      if (!data.choices?.[0]?.message?.content) {
        throw new Error('HunYuan响应中没有找到有效内容');
      }
      
      const content = data.choices[0].message.content.trim();
      
      // 如果内容是markdown格式的JSON，需要提取JSON部分
      const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/);
      const jsonContent = jsonMatch ? jsonMatch[1].trim() : content;
      
      try {
        // 尝试将内容解析为JSON
        const sceneData = JSON.parse(jsonContent);
        return sceneData as SceneData;
      } catch (parseError) {
        // 如果解析失败，则将内容作为普通文本处理
        this.logger.warn('解析HunYuan响应为JSON失败，使用文本格式', String(parseError));
        
        return {
          scene_text: content,
          npc_dialogue: "",
          choices: [
            {
              text: "继续",
              next_scene: "continue",
              requirements: null
            }
          ]
        };
      }
    } catch (error) {
      this.logger.error('处理HunYuan响应时出错', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }
} 